import { Error as HttpError, formBody, request } from "./http";
import { backendOnly, loginPath } from "./frontend-mode";

export const backendOrigin = (): string | null => {
    const value = document.querySelector<HTMLMetaElement>(`meta[name="energikilden-backend"]`)?.content;
    if (!value) return null;
    const url = new URL(value);
    if (url.protocol !== `https:` || url.origin !== value) throw new Error(`Ugyldig innloggingsoppsett.`);
    return value;
};

export const backendAuthenticationEnabled = (): boolean =>
    document.querySelector(`meta[name="energikilden-backend-authentication"]`) !== null;

const api = (path: string, method: string, body?: unknown) => {
    const origin = backendOrigin();
    if (!origin || !path.startsWith(`/api/`)) throw new Error(`Innloggingstjenesten er ikke tilgjengelig.`);
    return request(origin + path, {
        method, credentials: `include`, cache: `no-store`,
        headers: { "Content-Type": `application/json`, "X-Energikilden-Request": `1` },
        body: body === undefined ? undefined : JSON.stringify(body)
    });
};

const base64Url = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, `-`).replace(/\//g, `_`).replace(/=+$/, ``);

const pkce = async () => {
    const verifier = base64Url(crypto.getRandomValues(new Uint8Array(32)));
    const digest = await crypto.subtle.digest(`SHA-256`, new TextEncoder().encode(verifier));
    return { verifier, challenge: base64Url(new Uint8Array(digest)) };
};

const readCode = async (response: Response) => {
    const value: unknown = await response.json();
    if (!value || typeof value !== `object` || !(`code` in value) ||
        typeof value.code !== `string` || !/^[A-F0-9]{64}$/.test(value.code)) {
        throw new Error(`Innloggingstjenesten svarte uventet. Prøv igjen.`);
    }
    return value.code;
};

let exchanging: Promise<void> | undefined;
const establishBackendSession = () => {
    if (!exchanging) exchanging = (async () => {
        const { verifier, challenge } = await pkce();
        const code = await readCode(await request(`session-exchange.asp`, {
            method: `POST`, headers: { "X-Energikilden-Request": `1` }, body: formBody({ challenge })
        }));
        await api(`/api/sessions`, `POST`, { code, verifier });
    })().finally(() => { exchanging = undefined; });
    return exchanging;
};

export const backendRequest = async (path: string, method: string, body?: unknown, authenticated = true) => {
    try {
        return await api(path, method, body);
    } catch (error) {
        if (backendOnly()) {
            if (authenticated && error instanceof HttpError && error.status === 401) location.assign(loginPath());
            throw error;
        }
        // An ASP-authenticated browser may not have acquired its API cookie yet. A 401 cannot have applied a write.
        if (!authenticated || !(error instanceof HttpError) || error.status !== 401) throw error;
        await establishBackendSession();
        return api(path, method, body);
    }
};

export const restoreLegacySession = async () => {
    const { verifier, challenge } = await pkce();
    const code = await readCode(await api(`/api/account/legacy-exchanges`, `POST`, { challenge }));
    await request(`session-restore.asp`, {
        method: `POST`, headers: { "X-Energikilden-Request": `1` }, body: formBody({ code, verifier })
    });
};

export const authenticationMessage = (error: unknown) => {
    if (error instanceof HttpError) {
        if (error.status === 429) return `For mange forsøk. Vent litt før du prøver igjen.`;
        if (error.status === 401) return `Brukernavn, e-postadresse eller passord er feil, eller kontoen er ikke aktiv.`;
        if (error.status === 409) return `Opplysningene er allerede i bruk. Velg noe annet.`;
        if (error.status === 400) return `Opplysningene eller lenken er ugyldig. Prøv igjen eller be om en ny lenke.`;
    }
    return `Tjenesten er ikke tilgjengelig akkurat nå. Prøv igjen senere.`;
};

export const passwordResultMessage = async (response: Response): Promise<string | null> => {
    if (response.status === 204) return null;
    const result: unknown = await response.json();
    const code = result && typeof result === `object` && `error` in result ? result.error : undefined;
    if (code === `password_too_common`) return `Dette passordet er for vanlig eller kjent fra en datalekkasje.`;
    if (code === `current_password_incorrect`) return `Det nåværende passordet er ikke riktig.`;
    if (code === `invalid_email`) return `Skriv inn en gyldig e-postadresse.`;
    if (code === `password_requirements`) return `Bruk 15–128 tegn, gjerne flere ord med mellomrom.`;
    return `Tjenesten svarte uventet. Prøv igjen.`;
};
